<template>
  <div class="tree-node">
    <div class="node-content">
      <div class="node-left">
        <!-- Expand/Collapse Button -->
        <button
          v-if="account.children && account.children.length > 0"
          class="expand-button"
          @click="toggleExpand"
        >
          <span class="expand-icon" :class="{ expanded: account.expanded }">
            ▶
          </span>
        </button>
        <div v-else class="expand-placeholder"></div>

        <!-- Folder/Document Icon -->
        <span class="account-icon">
          {{ account.type === 'category' ? '📁' : '📄' }}
        </span>

        <!-- Account Code and Name -->
        <div class="account-info">
          <span class="account-code">{{ account.code }}</span>
          <span class="account-name">{{ account.name }}</span>
        </div>
      </div>

      <!-- Balance and Badge -->
      <div class="node-right">
        <div class="balance-info">
          <span class="balance" :class="{ negative: account.balance < 0 }">
            {{ formatCurrency(Math.abs(account.balance)) }}
          </span>
          <span v-if="account.type === 'account'" class="balance-detail">
            Dr: {{ formatCurrency(account.totalDebit) }} | Cr: {{ formatCurrency(account.totalCredit) }}
          </span>
        </div>
        <span class="category-badge" :class="account.category.toLowerCase()">
          {{ account.category }}
        </span>
        <div class="node-actions">
          <button v-if="account.type !== 'account'" class="action-icon-btn add-btn" title="Add sub-account" @click.stop="onAdd(account)">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
          <button class="action-icon-btn edit-btn" title="Edit account" @click.stop="onEdit(account)">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </button>
          <button class="action-icon-btn delete-btn" title="Delete account" @click.stop="onDelete(account)">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Children -->
    <div v-if="account.expanded && account.children && account.children.length > 0" class="children">
      <TreeNode
        v-for="child in account.children"
        :key="child.id"
        :account="child"
        :onAdd="onAdd"
        :onEdit="onEdit"
        :onDelete="onDelete"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'TreeNode',
  props: {
    account: {
      type: Object,
      required: true
    },
    onAdd: {
      type: Function,
      required: true
    },
    onEdit: {
      type: Function,
      required: true
    },
    onDelete: {
      type: Function,
      required: true
    }
  },
  methods: {
    toggleExpand() {
      this.account.expanded = !this.account.expanded
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('en-PK', {
        style: 'currency',
        currency: 'PKR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value || 0)
    }
  }
}
</script>

<style scoped>
.tree-node {
  width: 100%;
}

.node-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background 0.2s ease;
}

.node-content:hover {
  background: #f9fafb;
}

.node-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.expand-button {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.expand-icon {
  font-size: 12px;
  color: #6b7280;
  transition: transform 0.2s ease;
  display: inline-block;
}

.expand-icon.expanded {
  transform: rotate(90deg);
}

.expand-placeholder {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.account-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.account-info {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.account-code {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  flex-shrink: 0;
}

.account-name {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.node-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  margin-left: 16px;
}

.balance-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 120px;
}

.balance {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  text-align: right;
}

.balance.negative {
  color: #dc2626;
}

.balance-detail {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
}

.category-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 4px;
  text-transform: capitalize;
  white-space: nowrap;
}

.category-badge.asset {
  background: #dbeafe;
  color: #0c4a6e;
}

.category-badge.liability {
  background: #fee2e2;
  color: #7f1d1d;
}

.category-badge.equity {
  background: #f3e8ff;
  color: #6b21a8;
}

.category-badge.revenue {
  background: #dcfce7;
  color: #166534;
}

.category-badge.expense {
  background: #fed7aa;
  color: #92400e;
}

.node-actions {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.node-content:hover .node-actions { opacity: 1; }

.action-icon-btn {
  width: 28px;
  height: 28px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.2s ease;
}

.action-icon-btn.add-btn:hover { color: #2563eb; border-color: #2563eb; background: #eff6ff; }
.action-icon-btn.edit-btn:hover { color: #16a34a; border-color: #16a34a; background: #f0fdf4; }
.action-icon-btn.delete-btn:hover { color: #dc2626; border-color: #dc2626; background: #fef2f2; }

.children {
  background: #fafbfc;
}

.children .tree-node:first-child .node-content {
  border-top: 1px solid #f3f4f6;
}

@media (max-width: 768px) {
  .node-content {
    padding: 10px 12px;
    flex-wrap: wrap;
  }

  .node-right {
    width: 100%;
    margin-left: 0;
    margin-top: 8px;
    padding-left: 32px;
  }

  .balance {
    min-width: auto;
  }
}
</style>
